import React, { useState, useEffect } from 'react';
import { Wallet, TrendingUp, TrendingDown, RefreshCw, Clock, User, Calendar, ArrowUpRight, ArrowDownLeft, CreditCard } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OwnerDepositDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(0);
  const [feePercentage, setFeePercentage] = useState(0);
  const [ownerName, setOwnerName] = useState('');
  const [ownerCode, setOwnerCode] = useState('');
  const [depositHistory, setDepositHistory] = useState([]);
  const [usageHistory, setUsageHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('deposits');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Fetch balance using new endpoint for current owner
      const balanceRes = await axios.get(
        `${API_URL}/api/owner/my-balance`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (balanceRes.data.success) {
        setBalance(balanceRes.data.data.balance || 0);
        setFeePercentage(balanceRes.data.data.fee_percentage || 0);
        setOwnerName(balanceRes.data.data.owner_name || '');
        setOwnerCode(balanceRes.data.data.owner_code || '');
      }

      // Fetch history using new endpoint
      const historyRes = await axios.get(
        `${API_URL}/api/owner/my-deposits`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (historyRes.data.success) {
        const all = historyRes.data.data || [];
        // Backend stores type as 'topup'|'usage'|'adjustment'.
        // Split adjustment by amount sign so positive adjust shows in deposit tab,
        // negative adjust shows in usage tab. Normalize display amount to positive.
        const deposits = all
          .filter(h => h.type === 'topup' || (h.type === 'adjustment' && Number(h.amount) > 0))
          .map(h => ({ ...h, amount: Math.abs(Number(h.amount) || 0) }));
        const usages = all
          .filter(h => h.type === 'usage' || (h.type === 'adjustment' && Number(h.amount) < 0))
          .map(h => ({ ...h, amount: Math.abs(Number(h.amount) || 0) }));
        setDepositHistory(deposits);
        setUsageHistory(usages);
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Gagal memuat data saldo');
    } finally {
      setLoading(false);
    }
  };

  const totalDeposits = depositHistory.reduce((sum, d) => sum + (d.amount || 0), 0);
  const totalUsage = usageHistory.reduce((sum, u) => sum + (u.amount || 0), 0);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto mb-3"></div>
          <p className="text-sm text-gray-600 dark:text-gray-400">Memuat data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Saldo & Deposit</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Kelola saldo untuk penagihan pelanggan
          </p>
        </div>
        <Button
          onClick={fetchData}
          variant="outline"
          size="sm"
          className="flex items-center gap-1.5 self-start sm:self-auto"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          <span className="text-xs">Refresh</span>
        </Button>
      </div>

      {/* Balance Card - Compact Version */}
      <Card className="bg-gradient-to-br from-indigo-500 via-indigo-600 to-purple-600 text-white overflow-hidden relative">
        <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
        <CardContent className="p-4 md:p-5 relative">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-white/20 rounded-lg">
                  <Wallet className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-[10px] text-indigo-100 uppercase tracking-wide">Saldo Tersedia</p>
                  {ownerCode && (
                    <p className="text-[10px] text-indigo-200">{ownerCode} - {ownerName}</p>
                  )}
                </div>
              </div>
              <p className="text-2xl md:text-3xl font-bold tracking-tight">
                {formatCurrency(balance)}
              </p>
              <div className="flex items-center gap-3 text-xs">
                <div className="flex items-center gap-1 bg-white/10 px-2 py-0.5 rounded-full">
                  <CreditCard className="h-3 w-3" />
                  <span>Fee: {feePercentage}%</span>
                </div>
              </div>
            </div>
            <div className="hidden sm:block p-3 bg-white/10 rounded-xl">
              <Wallet className="h-8 w-8 text-white/80" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid - Compact */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="border-green-100 dark:border-green-900/30">
          <CardContent className="p-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-50 dark:bg-green-900/30 rounded-lg shrink-0">
                <ArrowDownLeft className="h-4 w-4 text-green-600 dark:text-green-400" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">Total Deposit</p>
                <p className="text-sm md:text-base font-bold text-gray-900 dark:text-white truncate">
                  {formatCurrency(totalDeposits)}
                </p>
                <p className="text-[10px] text-gray-400 dark:text-gray-500">
                  {depositHistory.length} transaksi
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-red-100 dark:border-red-900/30">
          <CardContent className="p-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-50 dark:bg-red-900/30 rounded-lg shrink-0">
                <ArrowUpRight className="h-4 w-4 text-red-600 dark:text-red-400" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">Total Penggunaan</p>
                <p className="text-sm md:text-base font-bold text-gray-900 dark:text-white truncate">
                  {formatCurrency(totalUsage)}
                </p>
                <p className="text-[10px] text-gray-400 dark:text-gray-500">
                  {usageHistory.length} transaksi
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* History Card */}
      <Card>
        <CardHeader className="p-3 pb-0">
          <div className="flex border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setActiveTab('deposits')}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors border-b-2 -mb-px ${
                activeTab === 'deposits'
                  ? 'text-indigo-600 dark:text-indigo-400 border-indigo-600 dark:border-indigo-400'
                  : 'text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <TrendingUp className="h-3.5 w-3.5" />
              Deposit Masuk
            </button>
            <button
              onClick={() => setActiveTab('usage')}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors border-b-2 -mb-px ${
                activeTab === 'usage'
                  ? 'text-indigo-600 dark:text-indigo-400 border-indigo-600 dark:border-indigo-400'
                  : 'text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <TrendingDown className="h-3.5 w-3.5" />
              Penggunaan
            </button>
          </div>
        </CardHeader>
        <CardContent className="p-3">
          {activeTab === 'deposits' ? (
            <div className="space-y-2">
              {depositHistory.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
                    <Wallet className="h-6 w-6 text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Belum ada deposit</p>
                </div>
              ) : (
                depositHistory.map((deposit) => (
                  <div key={deposit.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-full shrink-0">
                        <ArrowDownLeft className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-gray-900 dark:text-white truncate">
                          {deposit.admin_name || 'Admin'}
                        </p>
                        <div className="flex items-center gap-2 text-[10px] text-gray-500 dark:text-gray-400">
                          <Clock className="h-3 w-3" />
                          <span>{formatDate(deposit.created_at)}</span>
                        </div>
                        {deposit.description && (
                          <p className="text-[10px] text-gray-400 dark:text-gray-500 truncate mt-0.5">{deposit.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0 ml-2">
                      <p className="text-sm font-semibold text-green-600 dark:text-green-400">
                        +{formatCurrency(deposit.amount)}
                      </p>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400">
                        Saldo: {formatCurrency(deposit.balance_after)}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {usageHistory.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
                    <TrendingDown className="h-6 w-6 text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Belum ada penggunaan</p>
                </div>
              ) : (
                usageHistory.map((usage) => (
                  <div key={usage.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full shrink-0">
                        <ArrowUpRight className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-gray-900 dark:text-white truncate">
                          {usage.description || 'Penggunaan Saldo'}
                        </p>
                        <div className="flex items-center gap-2 text-[10px] text-gray-500 dark:text-gray-400">
                          <Clock className="h-3 w-3" />
                          <span>{formatDate(usage.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right shrink-0 ml-2">
                      <p className="text-sm font-semibold text-red-600 dark:text-red-400">
                        -{formatCurrency(usage.amount)}
                      </p>
                      {usage.owner_fee > 0 && (
                        <p className="text-[10px] text-green-600 dark:text-green-400">
                          Fee: +{formatCurrency(usage.owner_fee)}
                        </p>
                      )}
                      <p className="text-[10px] text-gray-500 dark:text-gray-400">
                        Saldo: {formatCurrency(usage.balance_after)}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OwnerDepositDashboard;
